<template>
  <v-container fill-height>
    <v-layout align-center justify-center>
      <v-flex xs12 sm8 md4>
        <v-card class="elevation-12">
          <v-toolbar dark color="primary">
            <v-toolbar-title>{{ title }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <!-- Loading state -->
            <div v-if="loading" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" />
              <p class="mt-2">Validating link...</p>
            </div>

            <!-- Error state -->
            <div v-else-if="error">
              <v-alert type="error" text elevation="2">
                {{ error }}
              </v-alert>
              <v-row class="action-row">
                <v-spacer />
                <v-btn to="/forgotPassword" color="primary">
                  Request New Link
                </v-btn>
              </v-row>
            </div>

            <!-- Success state after password is set -->
            <div v-else-if="success">
              <v-alert type="success" text elevation="2">
                Your password has been set successfully!
              </v-alert>
              <v-row class="action-row">
                <v-spacer />
                <v-btn to="/login" color="primary">
                  Go to Login
                </v-btn>
              </v-row>
            </div>

            <!-- Password form -->
            <div v-else-if="tokenValid">
              <p class="mb-4">
                Setting password for <strong>{{ email }}</strong>
              </p>
              <v-form @submit.prevent="save">
                <v-text-field
                  prepend-icon="mdi-lock"
                  name="password"
                  label="New Password"
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  :rules="[v => !!v || 'Required']"
                  required
                  v-on:keydown.enter.prevent="save"
                />
                <v-checkbox v-model="showPassword" label="Show password" />

                <v-row class="action-row">
                  <v-spacer />
                  <v-btn
                    color="primary"
                    :loading="saving"
                    :disabled="!trimmed"
                    @click.prevent="save"
                    class="spaced-button"
                    type="submit"
                  >
                    Set Password
                  </v-btn>
                </v-row>
              </v-form>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import api from '@/api';

export default {
  name: 'ResetPassword',
  data() {
    return {
      loading: true,
      saving: false,
      tokenValid: false,
      success: false,
      error: null,
      token: null,
      tokenType: null,
      email: '',
      password: '',
      showPassword: true
    };
  },
  computed: {
    trimmed() {
      return this.password.trim();
    },
    title() {
      if (this.tokenType === 'invite') {
        return 'Set Your Password';
      }
      return 'Reset Password';
    }
  },
  async created() {
    this.$store.dispatch('setNavData', { title: 'Reset Password', backPath: '' });
    this.token = this.$route.query.token;
    await this.validateToken();
  },
  methods: {
    async validateToken() {
      if (!this.token) {
        this.loading = false;
        this.error = 'No token provided. Please use the link from your email.';
        return;
      }

      try {
        const result = await api.get('/auth/validateToken', {
          params: { token: this.token }
        });

        this.loading = false;

        if (result.data.valid) {
          this.tokenValid = true;
          this.tokenType = result.data.tokenType;
          this.email = result.data.email;
        } else {
          this.error = result.data.error || 'Invalid or expired link.';
        }
      } catch (err) {
        this.loading = false;
        this.error = 'Unable to validate the link. Please try again.';
      }
    },
    async save() {
      if (!this.trimmed) {
        return;
      }

      this.saving = true;

      try {
        const result = await api.post('/auth/resetPassword', {
          token: this.token,
          password: this.trimmed
        });

        this.saving = false;

        if (result.data.status === 'success') {
          this.success = true;
          this.$snotify.success('Your password has been set.', 'Success');
        } else if (result.data.error) {
          this.error = result.data.error;
        }
      } catch (err) {
        this.saving = false;
        if (err.response && err.response.data && err.response.data.error) {
          this.error = err.response.data.error;
        } else {
          this.error = 'Unable to set password. Please try again.';
        }
      }
    }
  }
};
</script>

<style scoped>
.action-row {
  margin-top: 16px;
}
</style>
